import React, { useState, useEffect } from 'react'

export default function AbdelrahmanPortfolio() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [typedText, setTypedText] = useState('');
  const roles = ['Backend Engineer', 'System Architect', 'Go Developer', 'Problem Solver'];
  const [roleIndex, setRoleIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Detect active section
      const sections = ['about', 'experience', 'projects', 'skills', 'achievements', 'contact'];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typing animation effect
  useEffect(() => {
    const currentRole = roles[roleIndex];
    let charIndex = 0;
    let isDeleting = false;

    const typeInterval = setInterval(() => {
      if (!isDeleting && charIndex <= currentRole.length) {
        setTypedText(currentRole.substring(0, charIndex));
        charIndex++;
      } else if (!isDeleting && charIndex > currentRole.length) {
        setTimeout(() => { isDeleting = true; }, 1500);
      } else if (isDeleting && charIndex > 0) {
        charIndex--;
        setTypedText(currentRole.substring(0, charIndex));
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        setRoleIndex((prev) => (prev + 1) % roles.length);
      }
    }, 100);

    return () => clearInterval(typeInterval);
  }, [roleIndex]);

  const experiences = [
    {
      title: 'Backend Software Engineer',
      company: 'TechyTypes — DoAnalysis Project',
      period: 'Sep 2025 – Present',
      highlights: [
        'Developed and maintained backend microservices using Go, following clean and layered architecture principles.',
        'Designed and implemented a reusable filtering layer shared across multiple services to support complex query composition efficiently.',
        'Worked extensively with MariaDB and MySQL, optimizing queries, indexes, and data access patterns.',
        'Integrated Redis caching to reduce database load and improve response times.',
        'Built and optimized Elasticsearch indexes and queries for advanced search, aggregation, and analytics.',
        'Implemented Kafka-based asynchronous workflows for high-volume event processing.',
        'Developed headless web scrapers using Node.js to ingest external data sources reliably.',
        'Created and managed cron jobs for scheduled scraping, data synchronization, and background processing.',
        'Applied system-level optimizations to improve scalability, stability, and overall performance.',
        'Identified performance bottlenecks and applied targeted optimizations that improved system scalability, stability, and overall responsiveness.',
        'Took ownership of backend features, collaborated closely with the team, reviewed code, and delivered production-ready solutions while ramping up quickly on a large existing codebase.',
      ],
      tools: ['Go', 'Node.js', 'MariaDB', 'MySQL', 'Redis', 'Elasticsearch', 'Kafka', 'Docker', 'Git', 'Microservices'],
      current: true,
      icon: '⚡',
    },
    {
      title: 'Full-Stack Developer',
      company: 'Meits Technology',
      period: 'Jun 2025 – Sep 2025',
      highlights: [
        'Built full-stack applications using React, NestJS, and PostgreSQL.',
        'Designed secure REST APIs and integrated third-party services.',
      ],
      tools: ['React', 'NestJS', 'PostgreSQL', 'REST APIs'],
      current: false,
      icon: '🚀',
    },
    {
      title: 'Software Engineer',
      company: 'MasteryHub',
      period: 'Feb 2025 – May 2025',
      highlights: [
        'Developed backend services using NestJS and Spring Boot.',
        'Worked with PostgreSQL, MySQL, and MongoDB.',
      ],
      tools: ['NestJS', 'Spring Boot', 'PostgreSQL', 'MySQL', 'MongoDB'],
      current: false,
      icon: '💡',
    },
    {
      title: 'Software Engineer Intern',
      company: 'Orange',
      period: 'Aug 2024 – Oct 2024',
      highlights: [
        'Developed Java Spring Boot applications and worked with SQL and NoSQL databases.',
      ],
      tools: ['Java', 'Spring Boot', 'SQL', 'NoSQL'],
      current: false,
      icon: '🎯',
    },
  ];

  const projects = [
    {
      title: 'Web Service Application',
      subtitle: 'Spring Boot',
      description: 'Built a RESTful backend service handling mobile recharge, internet services, and money transfers.',
      highlights: [
        'Applied layered architecture with MVC and Singleton patterns to ensure scalability and maintainability.',
        'Implemented authentication, validation, logging, and exception handling.',
        'Integrated MySQL for persistent storage and tested APIs using Postman and JUnit.',
      ],
      tools: ['Java', 'Spring Boot', 'MySQL', 'REST APIs', 'Postman', 'JUnit'],
      gradient: 'from-blue-500 via-cyan-500 to-teal-500',
      icon: '💳',
    },
    {
      title: 'News Application',
      subtitle: 'Full Stack with ML',
      description: 'Developed a backend using Node.js and MongoDB with a Flutter-based mobile frontend.',
      highlights: [
        'Implemented user authentication, article management, and personalized content recommendations.',
        'Integrated a Python-based machine learning model to detect and flag offensive comments in real time.',
        'Containerized the system using Docker for consistent deployment.',
      ],
      tools: ['Node.js', 'MongoDB', 'Flutter', 'Python', 'Docker'],
      gradient: 'from-purple-500 via-pink-500 to-rose-500',
      icon: '📰',
    },
    {
      title: 'Task Management Application',
      subtitle: 'Full Stack',
      description: 'Built a task management system using Spring Boot and React with role-based access control.',
      highlights: [
        'Integrated MongoDB for data storage and Docker for containerized deployment.',
        'Designed REST APIs and managed frontend state using React Context and Material UI.',
      ],
      tools: ['Spring Boot', 'React', 'MongoDB', 'Docker'],
      gradient: 'from-orange-500 via-amber-500 to-yellow-500',
      icon: '✅',
    },
  ];

  const skills = {
    'Programming Languages': { items: ['Go', 'Java', 'JavaScript', 'TypeScript', 'Python', 'C++'], icon: '💻' },
    'Backend Technologies': { items: ['Microservices', 'REST APIs', 'Kafka', 'Redis', 'Elasticsearch', 'Headless Scraping'], icon: '⚙️' },
    'Databases': { items: ['MariaDB', 'MySQL', 'PostgreSQL', 'MongoDB'], icon: '🗄️' },
    'Tools & DevOps': { items: ['Docker', 'Git', 'Linux', 'Postman', 'Jira'], icon: '🛠️' },
  };

  const achievements = [
    { title: 'Ranked 8th in ECPC 2023', subtitle: 'Qualified for ECPC Finals', icon: '🏆', color: 'from-yellow-400 to-amber-500' },
    { title: 'ECPC Finalist 2023', subtitle: 'Egyptian Collegiate Programming Contest', icon: '🥇', color: 'from-amber-400 to-orange-500' },
    { title: 'Ranked 29th in ECPC 2022', subtitle: 'Competitive Programming Achievement', icon: '🎯', color: 'from-blue-400 to-indigo-500' },
  ];

  const techLogos = [
    { name: 'Go', color: '#00ADD8' },
    { name: 'Java', color: '#ED8B00' },
    { name: 'Node.js', color: '#339933' },
    { name: 'React', color: '#61DAFB' },
    { name: 'Docker', color: '#2496ED' },
    { name: 'Redis', color: '#DC382D' },
    { name: 'Kafka', color: '#231F20' },
    { name: 'PostgreSQL', color: '#4169E1' },
  ];

  return (
    <main className="min-h-screen bg-gray-950 text-gray-100 font-sans overflow-x-hidden">
      {/* Enhanced Animated Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-gray-950 to-black"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[150px] animate-pulse-slow"></div>
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[180px] animate-pulse-slow animate-delay-200"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-600/20 rounded-full blur-[120px] animate-pulse-slow animate-delay-400"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary/5 to-accent/5 rounded-full blur-[100px]"></div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-gray-950/80 backdrop-blur-xl border-b border-white/5 py-3' : 'py-5'}`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-500"></div>
              <div className="relative w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-white font-bold text-sm">AT</div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm font-bold">Abdelrahman Tarek</h1>
              <p className="text-xs text-primary-400">Backend Software Engineer</p>
            </div>
          </div>
          <nav className="hidden md:flex gap-1 items-center bg-white/5 backdrop-blur-sm rounded-full px-2 py-1">
            {['About', 'Experience', 'Projects', 'Skills', 'Achievements', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`text-sm px-4 py-2 rounded-full transition-all duration-300 ${activeSection === item.toLowerCase() ? 'bg-primary text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`}
              >
                {item}
              </a>
            ))}
          </nav>
          <a href="mailto:Abdulrahmantarek0.1@gmail.com" className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-500"></div>
            <span className="relative btn-primary text-sm">
              <span>Hire Me</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </span>
          </a>
        </div>
      </header>

      {/* Hero Section - Enhanced */}
      <section className="min-h-screen flex items-center pt-20 relative">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in space-y-8">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 text-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
              <span className="text-gray-300">Available for opportunities</span>
            </div>

            <div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4 leading-[1.1]">
                Hi, I'm <span className="gradient-text">Abdelrahman</span>
              </h2>
              <div className="flex items-center gap-3 text-2xl md:text-3xl text-gray-400">
                <span>I'm a</span>
                <span className="text-white font-semibold min-w-[280px]">
                  {typedText}<span className="animate-pulse">|</span>
                </span>
              </div>
            </div>

            <p className="text-lg text-gray-400 leading-relaxed max-w-xl">
              Backend-focused Software Engineer crafting <span className="text-white font-medium">scalable, high-performance systems</span> using Go, Java, and Node.js. Specialized in microservices, distributed systems, and backend optimization.
            </p>

            <div className="flex flex-wrap gap-4">
              <a className="group relative" href="mailto:Abdulrahmantarek0.1@gmail.com">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-500"></div>
                <span className="relative btn-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  Let's Talk
                </span>
              </a>
              <a className="btn-secondary" href="https://github.com/abdelrahman1100" target="_blank" rel="noreferrer">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg>
                GitHub
              </a>
              <a className="btn-secondary" href="https://linkedin.com/in/abdelrahmantarek01" target="_blank" rel="noreferrer">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                LinkedIn
              </a>
            </div>

            {/* Tech Stack Pills */}
            <div className="pt-8 border-t border-white/10">
              <p className="text-xs text-gray-500 mb-4 uppercase tracking-wider">Tech Stack</p>
              <div className="flex flex-wrap gap-3">
                {techLogos.map((tech) => (
                  <div key={tech.name} className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:border-primary/50 hover:bg-primary/10 transition-all duration-300 cursor-default">
                    {tech.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Profile Photo Section - Enhanced */}
          <div className="flex justify-center lg:justify-end animate-fade-in animate-delay-200">
            <div className="relative group">
              {/* Outer glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary via-accent to-purple-600 rounded-[2rem] blur-2xl opacity-40 group-hover:opacity-60 transition duration-700 animate-pulse-slow"></div>

              {/* Photo container */}
              <div className="relative">
                <div className="w-72 h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-[2rem] bg-gradient-to-br from-primary via-accent to-purple-600 p-1 rotate-3 group-hover:rotate-0 transition-all duration-700 shadow-2xl shadow-primary/40">
                  <div className="w-full h-full rounded-[1.8rem] overflow-hidden bg-gray-900">
                    <img
                      src={process.env.PUBLIC_URL + '/profile.jpg'}
                      alt="Abdelrahman Tarek"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>

                {/* Floating badges */}
                <div className="absolute -bottom-4 -left-4 px-5 py-3 rounded-xl bg-gray-900/90 backdrop-blur-sm border border-white/10 shadow-xl animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      <span className="text-lg">🎓</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">Education</div>
                      <div className="text-sm font-semibold text-white">Cairo University '24</div>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 px-5 py-3 rounded-xl bg-gray-900/90 backdrop-blur-sm border border-white/10 shadow-xl animate-float animate-delay-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                      <span className="text-lg">✨</span>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400">GPA</div>
                      <div className="text-sm font-semibold text-white">3.1 / 4.0</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-1/2 -right-8 px-4 py-2 rounded-lg bg-primary/90 backdrop-blur-sm shadow-xl animate-float animate-delay-300">
                  <div className="text-xs font-semibold text-white">ECPC Finalist 🏆</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-500">Scroll</span>
          <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
        </div>
      </section>

      {/* About Section - Enhanced */}
      <section id="about" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">About Me</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3 space-y-6">
              <p className="text-xl text-gray-300 leading-relaxed">
                I'm a <span className="text-white font-semibold">Backend Software Engineer</span> passionate about building robust, scalable systems that perform under pressure.
              </p>
              <p className="text-gray-400 leading-relaxed">
                With hands-on experience in <span className="text-primary-300 font-medium">Go, Java, and Node.js</span>, I specialize in designing microservices architectures and optimizing backend performance. I'm comfortable owning backend services end-to-end and adapting quickly to large production systems.
              </p>
              <p className="text-gray-400 leading-relaxed">
                My experience spans from implementing complex filtering layers and caching strategies to building data ingestion pipelines and real-time search platforms using <span className="text-primary-300 font-medium">Redis, Elasticsearch, Kafka</span>, and headless scraping solutions.
              </p>

              {/* Quick facts */}
              <div className="grid grid-cols-2 gap-4 pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary-400">📍</div>
                  <div>
                    <div className="text-xs text-gray-500">Location</div>
                    <div className="text-sm font-medium">Giza / 6 October, Egypt</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center text-primary-400">🎓</div>
                  <div>
                    <div className="text-xs text-gray-500">Degree</div>
                    <div className="text-sm font-medium">B.S. Computer Science</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {[
                { value: '4+', label: 'Work Experiences', icon: '💼' },
                { value: '3+', label: 'Major Projects', icon: '🚀' },
                { value: 'Top 10', label: 'ECPC 2023', icon: '🏆' },
                { value: '6+', label: 'Languages', icon: '💻' },
              ].map((stat, i) => (
                <div key={i} className="group p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-primary/30 transition-all duration-300 card-hover">
                  <div className="text-2xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold gradient-text mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section - Enhanced Timeline */}
      <section id="experience" className="py-24 relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">Experience</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-accent to-purple-600 hidden md:block"></div>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <article key={index} className="relative group">
                  {/* Timeline dot */}
                  <div className="absolute left-6 w-5 h-5 rounded-full bg-gray-950 border-2 border-primary shadow-lg shadow-primary/30 hidden md:flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                  </div>

                  <div className="md:ml-20 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-primary/30 transition-all duration-500 card-hover">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="text-3xl">{exp.icon}</div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-xl font-bold text-white group-hover:text-primary-300 transition-colors">{exp.title}</h4>
                            {exp.current && (
                              <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium border border-green-500/30">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-primary-400 font-medium">{exp.company}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 font-medium md:text-right whitespace-nowrap bg-white/5 px-4 py-2 rounded-lg">{exp.period}</div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {exp.highlights.map((item, i) => (
                        <li key={i} className="flex gap-3 text-gray-400 text-sm">
                          <span className="text-primary-400 mt-0.5">▹</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-2">
                      {exp.tools.map((tool) => (
                        <span key={tool} className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary-300 text-xs font-medium border border-primary/20 hover:border-primary/40 transition-colors">{tool}</span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section - Enhanced Cards */}
      <section id="projects" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">Projects</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <article key={index} className="group relative rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden card-hover">
                {/* Gradient top bar */}
                <div className={`h-1.5 bg-gradient-to-r ${project.gradient}`}></div>

                {/* Hover glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                <div className="relative p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-3xl mb-3">{project.icon}</div>
                      <h4 className="text-lg font-bold text-white group-hover:text-primary-300 transition-colors">{project.title}</h4>
                      <div className="text-sm text-gray-500">{project.subtitle}</div>
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>

                  <ul className="space-y-2 mb-5">
                    {project.highlights.slice(0, 2).map((item, i) => (
                      <li key={i} className="flex gap-2 text-gray-500 text-xs">
                        <span className="text-gray-600 mt-0.5">•</span>
                        <span className="line-clamp-2">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-2">
                    {project.tools.slice(0, 4).map((tool) => (
                      <span key={tool} className="px-2.5 py-1 rounded-md bg-white/5 text-gray-400 text-xs border border-white/5">{tool}</span>
                    ))}
                    {project.tools.length > 4 && (
                      <span className="px-2.5 py-1 rounded-md bg-white/5 text-gray-500 text-xs">+{project.tools.length - 4}</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section - Enhanced */}
      <section id="skills" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">Skills</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(skills).map(([category, { items, icon }]) => (
              <div key={category} className="group p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-primary/30 transition-all duration-300 card-hover">
                <div className="flex items-center gap-3 mb-5">
                  <div className="text-2xl">{icon}</div>
                  <h4 className="text-lg font-semibold text-white">{category}</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map((skill) => (
                    <span key={skill} className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 text-gray-200 text-sm font-medium hover:border-primary/50 hover:from-primary/20 hover:to-accent/20 transition-all duration-300 cursor-default">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section - Enhanced */}
      <section id="achievements" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">Achievements</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <div key={index} className="group relative p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 overflow-hidden card-hover text-center">
                {/* Gradient glow on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${achievement.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                <div className="relative">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${achievement.color} text-3xl mb-4 shadow-lg`}>
                    {achievement.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{achievement.title}</h4>
                  <p className="text-sm text-gray-500">{achievement.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section - Enhanced */}
      <section id="contact" className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
            <h3 className="text-3xl font-bold">Get In Touch</h3>
            <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10">
              <h4 className="text-2xl font-bold text-white mb-4">Let's work together</h4>
              <p className="text-gray-400 mb-8">Interested in building something amazing? I'm always open to discussing new opportunities and exciting projects.</p>

              <div className="space-y-4">
                {[
                  { icon: '📧', label: 'Email', value: 'Abdulrahmantarek0.1@gmail.com', href: 'mailto:Abdulrahmantarek0.1@gmail.com' },
                  { icon: '📱', label: 'Phone', value: '+20 111 690 9615', href: 'tel:+201116909615' },
                  { icon: '💼', label: 'LinkedIn', value: 'linkedin.com/in/abdelrahmantarek01', href: 'https://linkedin.com/in/abdelrahmantarek01' },
                  { icon: '🐙', label: 'GitHub', value: 'github.com/abdelrahman1100', href: 'https://github.com/abdelrahman1100' },
                ].map((contact, i) => (
                  <a key={i} href={contact.href} target={contact.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-transparent hover:border-primary/30 transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center text-xl">
                      {contact.icon}
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">{contact.label}</div>
                      <div className="text-white font-medium">{contact.value}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="relative p-8 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/20 to-purple-600/20"></div>
              <div className="absolute inset-0 bg-gray-950/80 backdrop-blur-xl"></div>

              <div className="relative">
                <h4 className="text-xl font-bold text-white mb-6">Send a Message</h4>
                <form onSubmit={(e) => { e.preventDefault(); window.location.href = 'mailto:Abdulrahmantarek0.1@gmail.com'; }} className="space-y-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Your Name</label>
                    <input type="text" className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-primary/50 focus:bg-white/10 focus:outline-none transition-all duration-300" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Your Email</label>
                    <input type="email" className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-primary/50 focus:bg-white/10 focus:outline-none transition-all duration-300" placeholder="john@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Message</label>
                    <textarea rows={4} className="w-full p-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-primary/50 focus:bg-white/10 focus:outline-none transition-all duration-300 resize-none" placeholder="Hi Abdelrahman, I'm interested in..." />
                  </div>
                  <button type="submit" className="w-full group relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-500"></div>
                    <span className="relative btn-primary w-full justify-center py-4">
                      Send Message
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    </span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-xs">AT</div>
            <span className="text-gray-400 text-sm">© {new Date().getFullYear()} Abdelrahman Tarek. All rights reserved.</span>
          </div>
          <div className="flex gap-4">
            {[
              { href: 'https://github.com/abdelrahman1100', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg> },
              { href: 'https://linkedin.com/in/abdelrahmantarek01', icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg> },
              { href: 'mailto:Abdulrahmantarek0.1@gmail.com', icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> },
            ].map((social, i) => (
              <a key={i} href={social.href} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 hover:border-primary/30 transition-all duration-300">
                {social.icon}
              </a>
            ))}
          </div>
          <div className="text-gray-500 text-sm">Built with ❤️ and React</div>
        </div>
      </footer>
    </main>
  )
}
